import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { setPassiveTouchGestures } from '@polymer/polymer/lib/utils/settings';
// import 'core-js/es/array';
import '@polymer/paper-button/paper-button.js'

import { TesseractPromise } from '../../libs/tesseract.js';

import { defineElement } from '../../utility/defineElement.js';
import { template } from './lag-ocrscanner.html.js';

const FOCUS_AREA_WIDTH = 250;
const FOCUS_AREA_HEIGHT = 35;
const FOCUS_AREA_BORDER_COLOR = "#49fb35";

defineElement(class extends PolymerElement {

    static get is() { return 'lag-ocrscanner'; }

    static get template() { return template; }

    static get properties() {
        return {
            _scanStopped: { type: Boolean, value: false },
            _takePhotoTimeoutID: { type: Number },
            // stuff needed for the blur filter
            _blurRadius: { type: Number },
            _blurKernelSize: { type: Number },
            _blurKernel: { type: Int32Array },
            _blurMult: { type: Array }
        }
    }

    constructor() {
        super();
        setPassiveTouchGestures(true);
    }

    ready() {
        super.ready();
    }

    capture(sanitizeText = false) {
        return new Promise((resolve, reject) => {
            if (
                this.$.video_capture.videoWidth != undefined
                && this.$.video_capture.videoWidth > 0
                && this.$.video_capture.videoHeight != undefined
                && this.$.video_capture.videoHeight > 0
            ) {
                this.$.canvas.width = this.$.video_capture.videoWidth;
                this.$.canvas.height = this.$.video_capture.videoHeight;

                // this will set the video capture to display on the canvas
                let ctx = this.$.canvas.getContext("2d", { willReadFrequently: true });
                ctx.drawImage(this.$.video_capture, 0, 0);

                // here we setup a box area where the user should place the text to be scanned
                var pX = this.$.canvas.width / 2 - FOCUS_AREA_WIDTH / 2;
                var pY = this.$.canvas.height / 2 - FOCUS_AREA_HEIGHT / 2;

                this._cropImage(this.$.canvas, pX, pY, FOCUS_AREA_WIDTH, FOCUS_AREA_HEIGHT)
                    .then(dataUrl => {
                        TesseractPromise.then(Tesseract => {
                            Tesseract.recognize(
                                dataUrl, 'eng',
                                {
                                    // logger: m => console.log(m)
                                })
                                .catch(error => reject(error))
                                .then(result => resolve(sanitizeText ? this._sanitizeText(result.data.text) : result.data.text));
                        });
                    })
                    .catch(error => (error));

                this.stopCapture();

            }
        });
    }

    initCapture() {
        let video_constraints = { video: { facingMode: "environment" }, audio: false }
        this._scanStopped = false;

        navigator.mediaDevices.getUserMedia(video_constraints)
            .then((stream) => {
                this.$.video_capture.srcObject = stream;
                this._startCapture();
            })
            .catch((e) => {
                console.error(e);
            });
    }

    _startCapture() {
        if (!this._scanStopped) {
            clearTimeout(this._takePhotoTimeoutID);
            this._takePhotoTimeoutID = 0;
            this._doCapture();
        }
    }

    _doCapture() {
        if (this._takePhotoTimeoutID != undefined && !this._scanStopped) {
            this._previewCanvas();
            clearTimeout(this._takePhotoTimeoutID);
            this._takePhotoTimeoutID = setTimeout(() => {
                this._doCapture();
            }, 34);
        } else {
            this.stopCapture();
        }
    }

    stopCapture() {
        clearTimeout(this._takePhotoTimeoutID);
        this._takePhotoTimeoutID = undefined;
        this._scanStopped = true;

        (new Promise((resolve) => {
            let vc = this.$.video_capture;
            vc.srcObject.getVideoTracks().forEach(track => {
                track.stop();
                vc.srcObject.removeTrack(track);
            });

            resolve();
        })).then(() => this.$.video_capture.srcObject = null);

        let ctx = this.$.canvas.getContext("2d");
        ctx.clearRect(0, 0, this.$.canvas.width, this.$.canvas.height);
    }

    _previewCanvas() {
        if (
            this.$.video_capture.videoWidth != undefined
            && this.$.video_capture.videoWidth > 0
            && this.$.video_capture.videoHeight != undefined
            && this.$.video_capture.videoHeight > 0
        ) {
            this.$.canvas.width = this.$.video_capture.videoWidth;
            this.$.canvas.height = this.$.video_capture.videoHeight;

            // this will set the video capture to display on the canvas
            let ctx = this.$.canvas.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(this.$.video_capture, 0, 0);

            // here we setup a box area where the user should place the text to be scanned
            var pX = this.$.canvas.width / 2 - FOCUS_AREA_WIDTH / 2;
            var pY = this.$.canvas.height / 2 - FOCUS_AREA_HEIGHT / 2;

            ctx.rect(pX, pY, FOCUS_AREA_WIDTH, FOCUS_AREA_HEIGHT);
            ctx.lineWidth = "2";
            ctx.strokeStyle = FOCUS_AREA_BORDER_COLOR;
            ctx.stroke();
        }
    }

    _cropImage(canvas, offsetX, offsetY, width, height) {
        return new Promise((resolve, reject) => {
            try {
                var buffer = document.createElement('canvas');
                var b_ctx = buffer.getContext('2d');

                buffer.width = width;
                buffer.height = height;

                b_ctx.drawImage(canvas, offsetX, offsetY, width, height, 0, 0, buffer.width, buffer.height)
                b_ctx.putImageData(this._preprocessImage(buffer), 0, 0, offsetX, offsetY, width, height);

                resolve(buffer);
            } catch (err) {
                reject(err);
            }
        });
    }

    _sanitizeText(text) {
        return text.replace(/\s/g, "");
    }

    // Image pre processing derived from p5js methods

    _preprocessImage(canvas) {
        const ctx = canvas.getContext('2d');
        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        this._blurARGB(image.data, canvas, 1);
        this._dilate(image.data, canvas);
        this._invertColors(image.data);
        this._thresholdFilter(image.data, 0.4);
        return image;
    }

    // from https://github.com/processing/p5.js/blob/main/src/image/filters.js
    _thresholdFilter(pixels, level) {
        if (level === undefined) {
            level = 0.5;
        }

        const thresh = Math.floor(level * 255);

        for (let i = 0; i < pixels.length; i += 4) {
            const red = pixels[i];
            const green = pixels[i + 1];
            const blue = pixels[i + 2];

            const gray = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

            let value;
            if (gray >= thresh) {
                value = 255;
            } else {
                value = 0;
            }

            pixels[i] = pixels[i + 1] = pixels[i + 2] = value;
        }
    }

    _getARGB(data, i) {
        const offset = i * 4;
        return (
            ((data[offset + 3] << 24) & 0xff000000) |
            ((data[offset] << 16) & 0x00ff0000) |
            ((data[offset + 1] << 8) & 0x0000ff00) |
            (data[offset + 2] & 0x000000ff)
        );
    }

    _setPixels(pixels, data) {
        let offset = 0;
        for (let i = 0, al = pixels.length; i < al; i++) {
            offset = i * 4;
            pixels[offset + 0] = (data[i] & 0x00ff0000) >>> 16;
            pixels[offset + 1] = (data[i] & 0x0000ff00) >>> 8;
            pixels[offset + 2] = (data[i] & 0x000000ff);
            pixels[offset + 3] = (data[i] & 0xff000000) >>> 24;
        }
    }

    // from https://github.com/processing/p5.js/blob/main/src/image/filters.js
    _buildBlurKernel(r) {
        let radius = (r * 3.5) | 0;
        radius = radius < 1 ? 1 : radius < 248 ? radius : 248;

        if (this._blurRadius !== radius) {
            this._blurRadius = radius;
            this._blurKernelSize = (1 + this._blurRadius) << 1;
            this._blurKernel = new Int32Array(this._blurKernelSize);
            this._blurMult = new Array(this._blurKernelSize);

            for (let l = 0; l < this._blurKernelSize; l++) {
                this._blurMult[l] = new Int32Array(256);
            }

            let bk, bki;
            let bm, bmi;

            for (let i = 1, radiusi = radius - 1; i < radius; i++) {
                this._blurKernel[radius + i] = this._blurKernel[radiusi] = bki = radiusi * radiusi;
                bm = this._blurMult[radius + i];
                bmi = this._blurMult[radiusi--];
                for (let j = 0; j < 256; j++) {
                    bm[j] = bmi[j] = bki * j;
                }
            }

            bk = this._blurKernel[radius] = radius * radius;
            bm = this._blurMult[radius];

            for (let k = 0; k < 256; k++) {
                bm[k] = bk * k
            }
        }
    }

    // from https://github.com/processing/p5.js/blob/main/src/image/filters.js
    _blurARGB(pixels, canvas, radius) {
        const width = canvas.width;
        const height = canvas.height;
        const numPackedPixels = width * height;
        const argb = new Int32Array(numPackedPixels);

        for (let j = 0; j < numPackedPixels; j++) {
            argb[j] = this._getARGB(pixels, j);
        }

        let sum, cr, cg, cb, ca;
        let read, ri, ym, ymi, bk0;

        const a2 = new Int32Array(numPackedPixels);
        const r2 = new Int32Array(numPackedPixels);
        const g2 = new Int32Array(numPackedPixels);
        const b2 = new Int32Array(numPackedPixels);

        let yi = 0;

        this._buildBlurKernel(radius);

        let x, y, i;
        let bm;

        for (y = 0; y < height; y++) {
            for (x = 0; x < width; x++) {
                cb = cg = cr = ca = sum = 0;
                read = x - this._blurRadius;

                if (read < 0) {
                    bk0 = -read;
                    read = 0;
                } else {
                    if (read >= width) {
                        break;
                    }
                    bk0 = 0;
                }

                for (i = bk0; i < this._blurKernelSize; i++) {
                    if (read >= width) {
                        break;
                    }

                    const c = argb[read + yi];
                    bm = this._blurMult[i];
                    ca += bm[(c & -16777216) >>> 24];
                    cr += bm[(c & 16711680) >> 16];
                    cg += bm[(c & 65280) >> 8];
                    cb += bm[(c & 255)];
                    sum += this._blurKernel[i];
                    read++;
                }

                ri = yi + x;
                a2[ri] = ca / sum;
                r2[ri] = cr / sum;
                g2[ri] = cg / sum;
                b2[ri] = cb / sum;
            }

            yi += width;
        }

        yi = 0;
        ym = -this._blurRadius;
        ymi = ym * width;

        for (y = 0; y < height; y++) {
            for (x = 0; x < width; x++) {
                cb = cg = cr = ca = sum = 0;

                if (ym < 0) {
                    bk0 = ri = -ym;
                    read = x;
                } else {
                    if (ym >= height) {
                        break;
                    }

                    bk0 = 0;
                    ri = ym;
                    read = x + ymi;
                }

                for (i = bk0; i < this._blurKernelSize; i++) {
                    if (ri >= height) {
                        break;
                    }

                    bm = this._blurMult[i];
                    ca += bm[a2[read]];
                    cr += bm[r2[read]];
                    cg += bm[g2[read]];
                    cb += bm[b2[read]];
                    sum += this._blurKernel[i];
                    ri++;
                    read += width;
                }
                argb[x + yi] =
                    ((ca / sum) << 24) |
                    ((cr / sum) << 16) |
                    ((cg / sum) << 8) |
                    (cb / sum);
            }

            yi += width;
            ymi += width;
            ym++;
        }

        this._setPixels(pixels, argb);
    }

    _invertColors(pixels) {
        for (var i = 0; i < pixels.length; i += 4) {
            pixels[i] = pixels[i] ^ 255; // Invert Red
            pixels[i + 1] = pixels[i + 1] ^ 255; // Invert Green
            pixels[i + 2] = pixels[i + 2] ^ 255; // Invert Bleu
        }
    }

    // from https://github.com/processing/p5.js/blob/main/src/image/filters.js
    _dilate(pixels, canvas) {
        let currIdx = 0;
        const maxIdx = pixels.length ? pixels.length / 4 : 0;
        const out = new Int32Array(maxIdx);
        let currRowIdx, maxRowIdx, colOrig, colOut, currLum;

        let idxRight, idxLeft, idxUp, idxDown;
        let colRight, colLeft, colUp, colDown;
        let lumRight, lumLeft, lumUp, lumDown;

        while (currIdx < maxIdx) {
            currRowIdx = currIdx;
            maxRowIdx = currIdx + canvas.width;
            while (currIdx < maxRowIdx) {
                colOrig = colOut = this._getARGB(pixels, currIdx);
                idxLeft = currIdx - 1;
                idxRight = currIdx + 1;
                idxUp = currIdx - canvas.width;
                idxDown = currIdx + canvas.width;

                if (idxLeft < currRowIdx) {
                    idxLeft = currIdx;
                }
                if (idxRight >= maxRowIdx) {
                    idxRight = currIdx;
                }
                if (idxUp < 0) {
                    idxUp = 0;
                }
                if (idxDown >= maxIdx) {
                    idxDown = currIdx;
                }
                colUp = this._getARGB(pixels, idxUp);
                colLeft = this._getARGB(pixels, idxLeft);
                colDown = this._getARGB(pixels, idxDown);
                colRight = this._getARGB(pixels, idxRight);

                //compute luminance
                currLum =
                    77 * ((colOrig >> 16) & 0xff) +
                    151 * ((colOrig >> 8) & 0xff) +
                    28 * (colOrig & 0xff);
                lumLeft =
                    77 * ((colLeft >> 16) & 0xff) +
                    151 * ((colLeft >> 8) & 0xff) +
                    28 * (colLeft & 0xff);
                lumRight =
                    77 * ((colRight >> 16) & 0xff) +
                    151 * ((colRight >> 8) & 0xff) +
                    28 * (colRight & 0xff);
                lumUp =
                    77 * ((colUp >> 16) & 0xff) +
                    151 * ((colUp >> 8) & 0xff) +
                    28 * (colUp & 0xff);
                lumDown =
                    77 * ((colDown >> 16) & 0xff) +
                    151 * ((colDown >> 8) & 0xff) +
                    28 * (colDown & 0xff);

                if (lumLeft > currLum) {
                    colOut = colLeft;
                    currLum = lumLeft;
                }
                if (lumRight > currLum) {
                    colOut = colRight;
                    currLum = lumRight;
                }
                if (lumUp > currLum) {
                    colOut = colUp;
                    currLum = lumUp;
                }
                if (lumDown > currLum) {
                    colOut = colDown;
                    currLum = lumDown;
                }

                out[currIdx++] = colOut;
            }
        }
        this._setPixels(pixels, out);
    };
});
